<script setup lang="ts">
import type { TenantContext } from '~/types/types'
import { socialLinks } from '~/composables/useLandingTemplate'

/**
 * Tenant public-site footer — identity, description, socials, copyright.
 * Fully themed via --site-* tokens.
 */
const props = defineProps<{ tenant: TenantContext }>()

const { t } = useI18n()
const localePath = useLocalePath()
const socials = computed(() => socialLinks(props.tenant.landing))
const year = new Date().getFullYear()
</script>

<template>
  <footer class="border-t border-[var(--site-border)] bg-[var(--site-surface)]">
    <div class="mx-auto max-w-6xl px-6 py-12">
      <div class="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
        <div class="max-w-md">
          <div class="flex items-center gap-3">
            <img
              v-if="tenant.branding.logo_url"
              :src="tenant.branding.logo_url"
              :alt="`${tenant.name} logo`"
              class="size-10 rounded-[calc(var(--site-radius)*0.6)] object-contain"
            >
            <div>
              <p class="site-display text-base">
                {{ tenant.name }}
              </p>
              <p class="text-xs text-[var(--site-muted)]">
                {{ tenant.domain }}
              </p>
            </div>
          </div>
          <p class="mt-3 text-sm leading-relaxed text-[var(--site-muted)]">
            {{ tenant.landing.hero_subheadline || tenant.description }}
          </p>
        </div>

        <div
          v-if="socials.length"
          class="flex gap-2"
        >
          <a
            v-for="s in socials"
            :key="s.href"
            :href="s.href"
            target="_blank"
            rel="noopener"
            class="flex size-10 items-center justify-center rounded-[var(--site-radius)] border border-[var(--site-border)] text-[var(--site-muted)] transition hover:bg-[var(--site-accent-soft)] hover:text-[var(--site-fg)]"
          >
            <UIcon
              :name="s.icon"
              class="size-4"
            />
          </a>
        </div>
      </div>

      <div class="mt-10 flex flex-col gap-2 border-t border-[var(--site-border)] pt-4 text-xs text-[var(--site-muted)] sm:flex-row sm:items-center sm:justify-between">
        <span>© {{ year }} {{ tenant.name }}. {{ t('site.rights') }}</span>
        <!-- Quiet, out of a prospect's way: this site's own staff sign in,
             not the client portal (that lives in the header). -->
        <NuxtLink
          :to="localePath('/login')"
          class="hover:text-[var(--site-fg)] hover:underline"
        >
          {{ t('site.staff_login') }}
        </NuxtLink>
      </div>
    </div>
  </footer>
</template>
